import { faAdd } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CircularProgress, IconButton, Snackbar } from '@material-ui/core';
import { chain } from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import VoxelAlert from '../../components/voxel/Alert/VoxelAlert';
import VoxelButton from '../../components/voxel/Buttons/VoxelButton';
import { ContentTaskGq } from '../../graphql/projects/models/ContentTaskGq';
import { ContentTaskSortingTypeGq } from '../../graphql/projects/models/ContentTaskSortingTypeGq';
import { DomainTypeGq } from '../../graphql/projects/models/DomainTypeGq';
import { ProjectGq } from '../../graphql/projects/models/ProjectGq';
import { projectsApiService } from '../../graphql/projects/projectsApiService';
import { CommandGq } from '../../graphql/projects/queries/contentPlan/useGetContentTasksGq';
import { CurrentUserGq } from '../../graphql/users/models/CurrentUserGq';
import { useDebounceValue } from '../../logic/otherUtilities/useDebounceValue';
import useIsScrolling from '../../logic/otherUtilities/useIsScrolling';
import SerpResultsDialog, {
  DisplaySerpResult,
} from '../DataManagementPage/components/Dialogs/SerpResultsDialog';
import ProjectAppBar from '../ProjectPage/ProjectAppBar/ProjectAppBar';
import { ProjectTabChoices } from '../ProjectPage/ProjectPage';
import { useProjectPageTitle } from '../ProjectPage/useProjectPageTitle';
import MissingRankDomainOrScrapePage from './MissingRankDomainOrScrapePage';
import ContentPlanAddMissingDialog from './Components/Dialogs/ContentPlanAddMissingDialog';
import ContentPlanProcessingDialog from './Components/Dialogs/ContentPlanProcessingDialog/ContentPlanProcessingDialog';
import ProcessingIndicator from './Components/Dialogs/ContentPlanProcessingDialog/ProcessingIndicator';
import TagBasedPlanDialog from './Components/Dialogs/TagBasedPlanDialog';
import ContentTaskDialog from './Components/Dialogs/ContentTaskDialog/ContentTaskDialog';
import MetaRuleDialog from './Components/Dialogs/GenerateMetaDialog/MetaRuleDialog';
import MetaRuleListDialog from './Components/Dialogs/GenerateMetaDialog/MetaRuleListDialog';
import ContentTaskTable, { ranges } from './Components/Tables/ContentTaskTable/ContentTaskTable';
import NewContentPlanButton from './NewContentPlanButton';
import NewPlanPage from './NewPlanPage';
import CombinationPlanDialog from './Components/Dialogs/CombinationPlanDialog';
import VoxelTooltipShort from '../../components/voxel/Tooltip/VoxelTooltipShort';
import { Warning } from '@material-ui/icons';
import NewContentPlanDialog from './NewContentPlanDialog';
const deepEqual = require('deep-equal');

export interface ContentPlanPageProps {
  project: ProjectGq;
  user: CurrentUserGq;
  reload: () => Promise<unknown>;
}

export type ContentPlanType = 'tag' | 'crawl';
export type ContentPlanStatus = 'processing' | 'failed' | 'completed';

const ContentPlanPage: React.FC<ContentPlanPageProps> = ({ project, reload, user }) => {
  useProjectPageTitle('Plan', project);

  // Client domain
  const clientDomain = project.domains?.find((d) => d.type === DomainTypeGq.PROJECT);
  const noClientDomain = !clientDomain;

  // Get processing data
  const {
    contentUploads,
    contentTagPlans,
    unfinishedScrape,
    query: contentPlanQuery,
  } = projectsApiService.queries.useGetContentPlans(
    { projectId: project.id },
    { pollInterval: 5000 }
  );
  const contentTagPlanAnalysis = contentTagPlans?.length ? contentTagPlans[0] : undefined;
  const contentUploadAnalysis = contentUploads?.length ? contentUploads[0] : undefined;

  // Missing keywords
  const {
    keywordCount: missingKeywordCount,
    keywordsExample: missingKeywords,
    query: missingQuery,
  } = projectsApiService.queries.useGetContentMissingKeywords({ projectId: project.id });

  const allDataRetrieved = !!contentUploads && !!contentTagPlans && missingKeywordCount !== null;

  // Selected content task
  const history = useHistory();
  const params = useParams() as any;
  const taskId = params.taskId?.length ? +params.taskId : undefined; // The Task ID comes from the URL now

  // Get selected content task data
  const { query: selectedContentTaskQuery } = projectsApiService.queries.useGetContentTasks(
    {
      filtering: {
        projectId: project.id,
        taskId,
        approvedStatus: [],
        actions: [],
        tagFilters: [],
        keywordFilters: [],
        statusCodes: [],
      },
      rowsPerPage: 1,
      page: 0,
      sorting: {},
    },
    { skip: !taskId }
  ); // We need to get the task from backend, in case user got here with a link

  // Instant opening task
  // - This is used so that the dialog opens instantly when clicking a table row
  // - But then we have a backup with the contentTaskQuery in case the selected task isn't in the current view (e.g. when navigating here via a link)

  const [instantOpeningTask, setInstantOpeningTask] = useState<ContentTaskGq>();
  const validatedInstantOpeningTask =
    instantOpeningTask?.id === taskId ? instantOpeningTask : undefined; // Make sure the state matches
  const dataHasLoaded = !!selectedContentTaskQuery?.data?.projects?.getContentTasks?.contentTasks;
  const selectedContentTask = dataHasLoaded
    ? selectedContentTaskQuery?.data?.projects?.getContentTasks?.contentTasks[0]
    : validatedInstantOpeningTask;

  // Refetch keyword count upon creating a new plan
  useEffect(() => {
    if (!!missingKeywordCount) {
      missingQuery.refetch();
    }
  }, [contentUploadAnalysis?.contentUpload, contentTagPlanAnalysis?.contentTagPlan]);

  // Content plan progress
  let contentPlanType: ContentPlanType | undefined = undefined;
  let contentPlanStatus: ContentPlanStatus | undefined = undefined;

  if (!!contentUploadAnalysis?.contentUpload) {
    // Crawl plan
    contentPlanType = 'crawl';

    // Crawl plan status
    if (
      contentUploadAnalysis.contentUpload.addToExistingFailTime ||
      contentUploadAnalysis.contentUpload.csvParsingFailTime
    ) {
      contentPlanStatus = 'failed';
    } else if (contentUploadAnalysis.contentUpload.complete) {
      contentPlanStatus = 'completed';
    } else {
      contentPlanStatus = 'processing';
    }
  } else if (!!contentTagPlanAnalysis?.contentTagPlan) {
    // Tag plan
    contentPlanType = 'tag';

    // Tag plan status
    if (!!contentTagPlanAnalysis.contentTagPlan.duplicateCheckEndTime) {
      contentPlanStatus = 'completed';
    } else {
      contentPlanStatus = 'processing';
    }
  }

  // State actions
  const [useTagsOpen, setUseTagsOpen] = useState(false);
  const [useCombiOpen, setUseCombiOpen] = useState(false);
  const [addMissingOpen, setAddMissingOpen] = useState(false);
  const [processingOpen, setProcessingOpen] = useState(false);
  const [displaySerpResult, setDisplaySerpResult] = useState<DisplaySerpResult>({
    keywordId: undefined,
    rankNumber: undefined,
  });

  // Meta rules
  const [metaRuleListOpen, setMetaRuleListOpen] = useState(false);
  const [metaRuleOpen, setMetaRuleOpen] = useState(false);
  const [metaRuleId, setMetaRuleId] = useState<number>(null);

  const { query: metaRulesQuery } = projectsApiService.queries.useGetMetaRules({
    projectId: project.id,
    page: 0,
    rowsPerPage: 100,
  });

  const handleOpenMetaRules = useCallback(() => {
    // Reload the rules so they're up-to-date
    metaRulesQuery?.refetch().then((result) => {
      const metaRules = result.data.projects.getMetaRules.metaRules;
      // Open list if rules already exist
      if (metaRules.length > 0) {
        setMetaRuleListOpen(true);
        return;
      }
      // Open new rule dialog if no rules exist
      if (metaRules.length === 0) {
        setMetaRuleOpen(true);
        return;
      }
    });
  }, []);

  // Show rerun logic
  let showAddMissing = false;
  if (!!missingKeywordCount) {
    if (contentPlanStatus === 'completed' || contentPlanStatus === 'failed') {
      showAddMissing = true;
    }
    const startTime =
      contentUploadAnalysis?.contentUpload?.rerunStartTime ??
      contentTagPlanAnalysis?.contentTagPlan?.processingStartTime;
    if (
      !!startTime &&
      moment(startTime).toISOString() < moment.utc().add(-2, 'minutes').toISOString()
    ) {
      showAddMissing = true;
    }
  }

  // Content task table
  const initialTableFilteringState: CommandGq = {
    page: 0,
    rowsPerPage: 20,
    filtering: {
      projectId: project.id,
      actions: [],
      approvedStatus: [],
      statusCodes: [],
      urlSearchTerm: '',
      keywordFilters: [
        // Primary
        {
          orderMin: ranges.primary.orderMin,
          orderMax: ranges.primary.orderMax,
          searchTerm: '',
          englishSearchTerm: '',
        },
        // Secondary
        {
          orderMin: ranges.secondary.orderMin,
          orderMax: ranges.secondary.orderMax,
          searchTerm: '',
          englishSearchTerm: '',
        },
        // Longtail
        {
          orderMin: ranges.longTail.orderMin,
          orderMax: ranges.longTail.orderMax,
          searchTerm: '',
          englishSearchTerm: '',
        },
        // All
        {
          orderMin: ranges.all.orderMin,
          orderMax: ranges.all.orderMax,
          searchTerm: '',
          englishSearchTerm: '',
        },
      ],
      tagFilters: [],
    },
    sorting: {
      direction: 'desc',
      type: ContentTaskSortingTypeGq.TOTAL_VOLUME,
    },
  };

  const [tableFilteringState, setTableFilteringState] = useState<CommandGq>(
    initialTableFilteringState
  );

  // Debounce table command
  const debouncedTableCommand = useDebounceValue(tableFilteringState, 250);
  const tableCommandHasNotChanged = useMemo(() => {
    // Deep equal checks if a object "is like" another one
    // Normally a === b checks for reference equality
    // But deep equal works even if it's an exact copy
    return deepEqual(debouncedTableCommand, initialTableFilteringState);
  }, [debouncedTableCommand]);

  // Table data
  const {
    contentTasks,
    count,
    rowsPerPage,
    page,
    query: contentTasksQuery,
  } = projectsApiService.queries.useGetContentTasks(debouncedTableCommand ?? tableFilteringState, {
    pollInterval: 10000,
  });

  // Combined reload - so that everything on the page reloads properly after mutations
  const localReload = useCallback(
    () =>
      Promise.all([
        contentTasksQuery.refetch(),
        contentPlanQuery.refetch(),
        selectedContentTaskQuery.refetch(),
        reload(),
      ]),
    [contentTasksQuery.refetch, contentPlanQuery.refetch, selectedContentTaskQuery, reload]
  );

  const isScrolling = useIsScrolling();
  const showTags = useMemo(
    () =>
      chain(project.tagGroups)
        .filter((tg) => tg.inContentPlan)
        .orderBy([(tg) => tg.contentPlanOrder, (tg) => tg.id])
        .value(),
    [project.tagGroups]
  );

  const projectDomain = project.domains.find((d) => d.type === DomainTypeGq.PROJECT);
  const clientUrl = projectDomain?.protocol + projectDomain?.hostName;

  // Trigger scrape
  const hasNoScrape = projectDomain?.scrapeTriggered == false;
  const triggerScrapeMutation = projectsApiService.mutations.scraping.createScrapeUrls();
  const [scrapeLoading, setScrapeLoading] = useState(false);
  const handleCreateScrapeUrls = () => {
    setScrapeLoading(true);
    triggerScrapeMutation({ projectId: project.id })
      .then(reload)
      .finally(() => setScrapeLoading(false));
  };

  const [newContentPlanOpen, setNewContentPlanOpen] = useState(false);

  // Warning
  const warningKey = 'content_beta_' + project.id;
  //const [showWarning, setShowWarning] = useState(!localStorage.getItem(warningKey)?.length);
  const [showWarning, setShowWarning] = useState(false);
  const handleHide = () => {
    setShowWarning(false);
    localStorage.setItem(warningKey, 'false');
  };
  if (showWarning)
    return (
      <>
        <ProjectAppBar project={project} user={user} isScrolling={isScrolling} />
        <div className={'mt-14'} />
        <div className="h-14" />
        <div className="w-[500px] h-full mx-auto py-2 flex flex-row items-center justify-start gap-4">
          <VoxelAlert severity="warning" title="Revamp planned" iconPosition="top" iconSize="lg">
            The current content plan functionality is not actively supported. A complete redesign is
            planned at a later stage.
            <div className="h-4" />
            <VoxelButton color="warning" title="Continue" size="sm" onClick={handleHide} />
          </VoxelAlert>
        </div>
      </>
    );

  if (
    !project.progressHasContentPlan &&
    (noClientDomain || !project.progressHasSerp || !!unfinishedScrape || !projectDomain?.scrapeTriggered)
  ) {
    return (
      <MissingRankDomainOrScrapePage
        project={project}
        user={user}
        reload={reload}
        noDomain={noClientDomain}
        noRankData={!project.progressHasSerp}
        unfinishedScrape={unfinishedScrape}
        hasNoScrape={!projectDomain?.scrapeTriggered}
      />
    );
  }
  if (!contentPlanStatus && !contentPlanQuery.loading) {
    return (
      <NewPlanPage
        project={project}
        user={user}
        reload={reload}
        allDataRetrieved={allDataRetrieved}
        newContentPlanOpen={newContentPlanOpen}
        setNewContentPlanOpen={setNewContentPlanOpen}
      />
    );
  }

  return (
    <>
      <ProjectAppBar project={project} user={user} isScrolling={isScrolling} />
      <div className={'mt-16'} />

      <div className="w-full h-full mx-auto px-10 py-2 flex flex-row items-center justify-start gap-4">
        {!!contentPlanStatus && (
          <NewContentPlanButton
            setNewContentPlanOpen={setNewContentPlanOpen}
            hasExistingPlan={true}
          />
        )}
        {!!contentPlanStatus && contentPlanStatus !== 'completed' && (
          <ProcessingIndicator
            status={contentPlanStatus}
            openProgress={() => setProcessingOpen(true)}
          />
        )}
        {!!unfinishedScrape && (
          <VoxelAlert severity="warning">Scraping urls in progress: {unfinishedScrape}</VoxelAlert>
        )}
        {showAddMissing && (
          <VoxelButton
            title={'Add Missing keywords'}
            size={'md'}
            color="primary"
            onClick={() => setAddMissingOpen(true)}
            endIcon={<FontAwesomeIcon icon={faAdd} />}
          />
        )}
      </div>
      {allDataRetrieved && !!contentPlanStatus && (
        <div className="w-full h-full mx-auto px-8 pb-1 ">
          <ContentTaskTable
            project={project}
            disabled={!contentPlanStatus || contentPlanStatus === 'processing'}
            setSelectedContentTask={(contentTask) => {
              history.push(`/${project.id}/${ProjectTabChoices.Plan}/${contentTask.id}`);
              setInstantOpeningTask(contentTask);
            }}
            selectedContentTaskId={taskId}
            setDisplaySerpResult={setDisplaySerpResult}
            hasTagPlan={contentPlanType === 'tag'}
            filteringState={tableFilteringState}
            initialFilteringState={initialTableFilteringState}
            setFilteringState={setTableFilteringState}
            setAddMissingOpen={setAddMissingOpen}
            showAddMissing={showAddMissing}
            reload={localReload}
            contentTasks={contentTasks}
            rowsPerPage={rowsPerPage}
            count={count}
            page={page}
            filterNotChanged={tableCommandHasNotChanged}
            openMetaRules={handleOpenMetaRules}
          />
        </div>
      )}
      {allDataRetrieved && !!contentPlanStatus && (
        <ContentPlanProcessingDialog
          openDialog={processingOpen}
          setOpenDialog={setProcessingOpen}
          uploadAnalysis={contentUploadAnalysis}
          tagPlanAnalysis={contentTagPlanAnalysis}
          status={contentPlanStatus}
          type={contentPlanType}
        />
      )}
      {allDataRetrieved && (
        <TagBasedPlanDialog
          openDialog={useTagsOpen}
          setOpenDialog={setUseTagsOpen}
          project={project}
          setProcessingOpen={setProcessingOpen}
          reload={localReload}
        />
      )}
      {allDataRetrieved && (
        <CombinationPlanDialog
          openDialog={useCombiOpen}
          setOpenDialog={setUseCombiOpen}
          project={project}
          setProcessingOpen={setProcessingOpen}
          reload={reload}
        />
      )}
      {!!showAddMissing && (
        <ContentPlanAddMissingDialog
          openDialog={addMissingOpen}
          setOpenDialog={setAddMissingOpen}
          setProgressOpen={setProcessingOpen}
          missingKeywordCount={missingKeywordCount}
          missingKeywords={missingKeywords}
          project={project}
          uploadId={contentUploadAnalysis?.contentUpload?.id}
          tagPlanId={contentTagPlanAnalysis?.contentTagPlan?.id}
          reload={localReload}
        />
      )}
      {metaRuleListOpen && !!metaRulesQuery?.data?.projects?.getMetaRules?.metaRules && (
        <MetaRuleListDialog
          openDialog={metaRuleListOpen}
          setOpenDialog={setMetaRuleListOpen}
          setMetaRuleId={setMetaRuleId}
          project={project}
          reload={localReload}
          clientUrl={project.domains.find((d) => d.type === DomainTypeGq.PROJECT)?.hostName}
          setMetaRuleOpen={setMetaRuleOpen}
        />
      )}
      {(metaRuleOpen || metaRuleId !== null) && (
        <MetaRuleDialog
          openDialog={metaRuleOpen || metaRuleId !== null}
          setOpenDialog={setMetaRuleOpen}
          project={project}
          metaRuleId={metaRuleId}
          setMetaRuleId={setMetaRuleId}
          reload={localReload}
        />
      )}
      {!!selectedContentTask && !(!contentPlanStatus || contentPlanStatus === 'processing') && (
        <ContentTaskDialog
          project={project}
          openDialog
          close={() => history.push(`/${project.id}/plan`)}
          contentTask={selectedContentTask}
          clientUrl={clientUrl}
          reload={localReload}
          showTags={showTags}
        />
      )}
      {!!displaySerpResult.keywordId && (
        <SerpResultsDialog
          project={project}
          setDisplaySerpResult={setDisplaySerpResult}
          displaySerpResult={displaySerpResult}
        />
      )}
      {newContentPlanOpen && (
        <NewContentPlanDialog
          project={project}
          unfinishedScrape={unfinishedScrape}
          setUseTagsOpen={setUseTagsOpen}
          setCombiOpen={setUseCombiOpen}
          setProcessingOpen={setProcessingOpen}
          reload={reload}
          hasExistingPlan={true}
          newContentPlanOpen={newContentPlanOpen}
          setNewContentPlanOpen={setNewContentPlanOpen}
        />
      )}
      {!!contentPlanStatus && (
        <Snackbar
          open={contentPlanStatus === 'processing'}
          message={'Editing is disabled while processing'}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        />
      )}
      {!!hasNoScrape && !project.scrapeCopyInProgress && (
        <VoxelTooltipShort
          message="Trigger scraping - This old project has no scraping data"
          disabled={scrapeLoading}
        >
          <IconButton onClick={handleCreateScrapeUrls}>
            {scrapeLoading ? <CircularProgress size={22} /> : <Warning />}
          </IconButton>
        </VoxelTooltipShort>
      )}
    </>
  );
};

export default ContentPlanPage;
